import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CandidateLayout from '../../layouts/CandidateLayout';
import Spinner from '../../components/common/Spinner';
import { listExams } from '../../api/candidate.api';

const STATUS_CONFIG = {
  completed:    { label: 'Completed',    classes: 'bg-emerald-100 text-emerald-700' },
  in_progress:  { label: 'In Progress',  classes: 'bg-amber-100 text-amber-700' },
  not_attempted:{ label: 'Not Attempted',classes: 'bg-slate-100 text-slate-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_attempted;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.classes}`}>
      {cfg.label}
    </span>
  );
};

const ExamCard = ({ exam }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-card hover:shadow-card-md transition-all duration-200 flex flex-col overflow-hidden group">
    {/* Color top strip */}
    <div className="h-1.5 bg-primary-gradient" />
    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h2 className="text-base font-bold text-slate-800 group-hover:text-primary-700 transition-colors">
          {exam.title}
        </h2>
        <StatusBadge status={exam.candidate_status} />
      </div>
      {exam.description && (
        <p className="text-slate-500 text-sm mb-4 flex-1 leading-relaxed line-clamp-2">
          {exam.description}
        </p>
      )}
      {!exam.description && <div className="flex-1" />}

      {/* Meta chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {exam.duration_minutes} min
        </span>
        <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {exam.question_count} questions
        </span>
        <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {exam.total_marks} marks
        </span>
      </div>

      <Link
        to={`/candidate/exams/${exam.id}/lobby`}
        className="w-full inline-flex items-center justify-center gap-2 bg-primary-gradient text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity shadow-md shadow-primary-600/20"
      >
        View Exam
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  </div>
);

const ExamList = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listExams()
      .then((res) => setExams(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <CandidateLayout>
      <div className="mb-7">
        <h1 className="text-2xl font-bold text-slate-900">Available Exams</h1>
        <p className="text-slate-500 text-sm mt-1">Select an exam to view instructions and begin.</p>
      </div>

      {loading ? (
        <Spinner className="py-20" />
      ) : exams.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-4">📋</div>
          <h3 className="text-slate-700 font-semibold mb-1">No exams available</h3>
          <p className="text-slate-400 text-sm">There are no published exams for you at this time.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {exams.map((exam) => (
            <ExamCard key={exam.id} exam={exam} />
          ))}
        </div>
      )}
    </CandidateLayout>
  );
};

export default ExamList;
